import { useState } from "react";
import { getPricingTiers, type Tier } from "@mw/shared/pricing";
import { TabPanel } from "react-aria-components";
import { useTranslations } from "next-intl";

import { CornerMarkerDot } from "../core/CornerMarkerDot";
import { PricingCardV3 } from "./PricingCardV3";
import { setStarterTierPromotion } from "./utils";
import { usePricingTableContext } from "./PricingTableContext";
import { ScrollablePricingCardsGrid, type CardItem } from "./ScrollablePricingCardsGrid";

export type PlatformVariant = "all" | "creative_platform" | "agents_platform" | "api";
export type BillingCycle = "monthly" | "yearly";
export type PlanType = "individual" | "business";
export type PricingDisplay = "tiers" | "agent-calls";

interface PricingCardsListV3Props {
  showStarterPromo?: boolean;
  filterTiers?: "individual" | "business" | "all";
  showFooter?: boolean;
  /** When true, renders content directly without TabPanel wrappers (use when parent handles tabs) */
  withoutTabs?: boolean;
  /** Controls what price to display: "tiers" shows tier price, "agent-calls" shows hosting cost per minute */
  pricingDisplay?: PricingDisplay;
  /** Whether to show the credits included section at the bottom of each card. Defaults to true. */
  showCreditsIncluded?: boolean;
}

type CardVariant = "creative_platform" | "agents_platform" | "api" | "simple";

/**
 * Gets tiers to display based on filter and the active pricing set.
 */
function getTiersToShow(filterTiers: "individual" | "business" | "all"): Tier[] {
  const allTiers = getPricingTiers();
  const consumerTiers = allTiers.filter(tier => ["free", "starter", "creator", "pro"].includes(tier.planId));
  const businessTiers = allTiers.filter(tier => ["enterprise", "scale", "scale_2024_08_10"].includes(tier.planId));

  if (filterTiers === "individual") {
    return consumerTiers;
  }
  if (filterTiers === "business") {
    return businessTiers;
  }
  // filterTiers === "all": show all tiers
  return [...consumerTiers, ...businessTiers];
}
// Card header height (from PricingCardV3)
const CARD_HEADER_HEIGHT_REM = 12.5;
const BUTTON_VERTICAL_POSITION_REM = CARD_HEADER_HEIGHT_REM / 2;

// The "creator" tier is the default highlighted (popular) card
const DEFAULT_HIGHLIGHTED_TIER = "creator";

export const PricingCardsListV3 = ({
  showStarterPromo,
  filterTiers = "all",
  showFooter = true,
  withoutTabs = false,
  pricingDisplay = "tiers",
  showCreditsIncluded = true,
}: PricingCardsListV3Props) => {
  const tPricing = useTranslations("PricingClientKeys.Pricing");
  const tGlobals = useTranslations("ClientKeys.Globals");
  const { selectedPlatform, billingCycle, planType } = usePricingTableContext();

  // Track which card is highlighted - persists on hover and stays when leaving
  const [highlightedTierId, setHighlightedTierId] = useState<string>(DEFAULT_HIGHLIGHTED_TIER);

  if (showStarterPromo && (planType === "individual" || filterTiers === "individual")) {
    const consumerTiers = getPricingTiers().filter(tier => ["free", "starter", "creator", "pro"].includes(tier.planId));
    setStarterTierPromotion([...consumerTiers]);
  }

  const scrollLabels = {
    previousSlide: tGlobals("previous_slide"),
    nextSlide: tGlobals("next_slide"),
  };

  const renderTierGrid = (variant: CardVariant) => {
    const tiers = getTiersToShow(filterTiers);

    // Unified layout (filterTiers === "all") - use ScrollablePricingCardsGrid with peek sizing and nav buttons
    if (filterTiers === "all") {
      const items: CardItem[] = tiers.map(tier => ({
        id: tier.planId,
        content: (
          <PricingCardV3
            tier={tier}
            variant={variant}
            billingCycle={billingCycle}
            pricingDisplay={pricingDisplay}
            showCreditsIncluded={showCreditsIncluded}
            highlighted={highlightedTierId === tier.planId}
            onHoverStart={() => setHighlightedTierId(tier.planId)}
          />
        ),
      }));

      return (
        <ScrollablePricingCardsGrid
          items={items}
          scrollLabels={scrollLabels}
          cardWidthClassName='tw-min-w-[250px] tw-flex-1 lg:tw-w-[23%] lg:tw-min-w-0 lg:tw-flex-none'
          buttonVerticalPositionRem={BUTTON_VERTICAL_POSITION_REM}
          showBottomBorder={false}
          itemClassName='tw-pb-4'
        />
      );
    }

    // Split layout - simple scrollable grid without peek sizing or nav buttons
    const showBusinessTitle = filterTiers === "business";

    return (
      <div className='tw-overflow-x-auto tw-overflow-y-hidden tw-scrollbar-none tw-py-2 -tw-my-2'>
        <div className='tw-flex tw-flex-nowrap'>
          {showBusinessTitle && (
            <div className='tw-relative tw-min-w-[250px] tw-flex-1 tw-p-4'>
              <div className='tw-flex tw-items-start tw-h-full tw-pt-4'>
                <h3
                  className='tw-font-normal tw-mb-2 f-heading-03'
                  dangerouslySetInnerHTML={{ __html: tPricing.raw("for_businesses") }}
                />
              </div>
              <div className='tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-w-[1px] bg-overlay-dark'>
                <CornerMarkerDot backgroundColor='white' position='topCentered' />
                <CornerMarkerDot backgroundColor='white' position='bottomCentered' />
              </div>
            </div>
          )}

          {tiers.map((tier, index) => {
            const isLast = index === tiers.length - 1;
            return (
              <div key={tier.planId} className='tw-relative tw-min-w-[250px] tw-flex-1 tw-p-4'>
                <PricingCardV3
                  tier={tier}
                  variant={variant}
                  billingCycle={billingCycle}
                  pricingDisplay={pricingDisplay}
                  showCreditsIncluded={showCreditsIncluded}
                  highlighted={highlightedTierId === tier.planId}
                  onHoverStart={() => setHighlightedTierId(tier.planId)}
                />

                {!isLast && (
                  <div className='tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-w-[1px] bg-overlay-dark'>
                    <CornerMarkerDot backgroundColor='white' position='topCentered' />
                    <CornerMarkerDot backgroundColor='white' position='bottomCentered' />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /** Map selectedPlatform to card variant */
  const getVariantForPlatform = (platform: PlatformVariant): CardVariant => {
    if (platform === "all") return "simple";
    return platform;
  };

  const footer = showFooter && (
    <div className='tw-relative tw-border-t tw-py-6 tw-text-center border-overlay-dark'>
      <CornerMarkerDot position='topLeft' />
      <CornerMarkerDot position='bottomLeft' />
      <CornerMarkerDot position='topRight' />
      <CornerMarkerDot position='bottomRight' />
      <p className='tw-text-gray-600 f-paragraph-03'>{tPricing("actualPriceNotice")}</p>
    </div>
  );

  // When withoutTabs is true, render content directly based on selectedPlatform
  if (withoutTabs) {
    return (
      <>
        {renderTierGrid(getVariantForPlatform(selectedPlatform))}
        {footer}
      </>
    );
  }

  return (
    <>
      <TabPanel id='all'>{renderTierGrid("simple")}</TabPanel>
      <TabPanel id='creative_platform'>{renderTierGrid("creative_platform")}</TabPanel>
      <TabPanel id='agents_platform'>{renderTierGrid("agents_platform")}</TabPanel>
      <TabPanel id='api'>{renderTierGrid("api")}</TabPanel>
      {footer}
    </>
  );
};
